import { useEffect, useState, type FormEvent } from "react";
import { supabase, functionErrorMessage } from "../lib/supabase.js";
import { BusinessIndustries } from "./BusinessIndustries.js";
import { AddClient } from "./AddClient.js";
import { Button, Input, Select } from "../lib/ui.js";

const CLIENT_STATUSES = [
  { value: "active", label: "Active" },
  { value: "suspended", label: "Suspended" },
  { value: "cancelled", label: "Cancelled" },
] as const;

/** Same shape check the database makes (migration 0044). */
const EMAIL_SHAPE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

function clientErrorMessage(message: string): string {
  if (message.includes("client_name_invalid")) return "A client name needs to be between 1 and 100 characters.";
  if (message.includes("billing_email_invalid")) return "That doesn't look like an email address.";
  if (message.includes("client_status_invalid")) return "That isn't a valid status.";
  if (message.includes("client_not_found")) return "That client no longer exists.";
  if (message.includes("not_authorized")) return "Only a platform admin can edit a client.";
  return message || "That couldn't be saved.";
}

interface ClientRow {
  id: string;
  name: string;
  billing_email: string;
  status: string;
}

interface BusinessRow {
  id: string;
  name: string;
  clients: { name: string } | null;
}

interface MemberRow {
  user_id: string;
  email: string | null;
  phone: string | null;
  role: string;
}

const ROLE_NAME: Record<string, string> = { owner: "an owner", manager: "a manager", viewer: "a viewer (read-only)", staff: "staff" };

export function ClientsList() {
  const [rows, setRows] = useState<ClientRow[]>([]);
  const [businesses, setBusinesses] = useState<BusinessRow[]>([]);
  const [businessId, setBusinessId] = useState("");
  /** The selected business's logins (email, or the mobile number for a mobile-only login). Null while loading. */
  const [members, setMembers] = useState<MemberRow[] | null>(null);
  const [email, setEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"owner" | "manager" | "viewer">("owner");
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState({ name: "", email: "", status: "active" });
  const [clientSaving, setClientSaving] = useState(false);
  const [clientError, setClientError] = useState<string | null>(null);
  const [clientMessage, setClientMessage] = useState<string | null>(null);

  function loadLists(selectFirst: boolean) {
    supabase
      .from("clients")
      .select("id, name, billing_email, status")
      .order("name")
      .then(({ data }) => setRows((data ?? []) as ClientRow[]));
    supabase
      .from("businesses")
      .select("id, name, clients(name)")
      .order("name")
      .then(({ data }) => {
        const list = (data ?? []) as unknown as BusinessRow[];
        setBusinesses(list);
        if (selectFirst && list[0]) setBusinessId(list[0].id);
      });
  }

  useEffect(() => {
    loadLists(true);
  }, []);

  async function loadOwners(id: string) {
    setMembers(null);
    const { data } = await supabase.rpc("rpc_list_business_members", { p_business_id: id });
    setMembers((data ?? []) as MemberRow[]);
  }

  /** Upgrade or change a login's permission, e.g. a free online-booking client (a viewer) who takes the phone service. */
  async function changeRole(m: MemberRow, role: string) {
    setError(null);
    setMessage(null);
    const { error: err } = await supabase.rpc("rpc_set_business_member_role", { p_business_id: businessId, p_user_id: m.user_id, p_role: role });
    if (err) setError(err.message.includes("last_owner") ? "There must always be at least one owner." : err.message);
    else setMessage(`${m.email || m.phone || "That login"} is now ${ROLE_NAME[role] ?? role}.`);
    await loadOwners(businessId);
  }

  useEffect(() => {
    if (businessId) void loadOwners(businessId);
  }, [businessId]);

  async function handleInvite(e: FormEvent) {
    e.preventDefault();
    if (!businessId) return;
    setError(null);
    setMessage(null);
    setSaving(true);
    const { error: err } = await supabase.functions.invoke("invite-member", {
      body: { business_id: businessId, email, role: inviteRole },
    });
    setSaving(false);
    if (err) {
      setError(await functionErrorMessage(err));
      return;
    }
    setMessage(`Invited ${email} as ${ROLE_NAME[inviteRole]}.`);
    setEmail("");
    void loadOwners(businessId);
  }

  function startEdit(r: ClientRow) {
    setEditingId(r.id);
    setDraft({ name: r.name, email: r.billing_email, status: r.status });
    setClientError(null);
    setClientMessage(null);
  }

  async function saveClient(r: ClientRow) {
    const name = draft.name.replace(/\s+/g, " ").trim();
    const billingEmail = draft.email.trim();
    if (!name || name.length > 100) return setClientError("A client name needs to be between 1 and 100 characters.");
    if (billingEmail.length > 254 || !EMAIL_SHAPE.test(billingEmail)) return setClientError("That doesn't look like an email address.");
    if (name === r.name && billingEmail === r.billing_email && draft.status === r.status) {
      setEditingId(null);
      return;
    }
    setClientSaving(true);
    setClientError(null);
    setClientMessage(null);
    const { error: err } = await supabase.rpc("rpc_admin_update_client", {
      p_client_id: r.id,
      p_name: name,
      p_billing_email: billingEmail,
      p_status: draft.status,
    });
    setClientSaving(false);
    if (err) {
      setClientError(clientErrorMessage(err.message));
      return;
    }
    setRows((all) => all.map((x) => (x.id === r.id ? { ...x, name, billing_email: billingEmail, status: draft.status } : x)));
    // The invite list shows each business with its client's name: reload it rather than guess which rows are this client's.
    void supabase
      .from("businesses")
      .select("id, name, clients(name)")
      .order("name")
      .then(({ data }) => setBusinesses((data ?? []) as unknown as BusinessRow[]));
    setEditingId(null);
    setClientMessage(`Saved ${name}.`);
  }

  return (
    <div>
      <h1 className="rc-h1" style={{ marginBottom: 16 }}>Clients</h1>
      <AddClient clients={rows.map((r) => ({ id: r.id, name: r.name }))} onChanged={() => loadLists(false)} />
      <div className="rc-table-scroll"><table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
        <thead>
          <tr style={{ textAlign: "left", borderBottom: "1px solid var(--rc-border)", color: "var(--rc-text-muted)" }}>
            <th style={{ padding: "10px 12px" }}>Name</th>
            <th style={{ padding: "10px 12px" }}>Billing email</th>
            <th style={{ padding: "10px 12px" }}>Status</th>
            <th style={{ padding: "10px 12px" }} />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) =>
            editingId === r.id ? (
              <tr key={r.id} style={{ borderBottom: "1px solid var(--rc-border-soft)" }}>
                <td style={{ padding: "10px 12px" }}>
                  <Input
                    aria-label={`Client name for ${r.name}`}
                    value={draft.name}
                    maxLength={100}
                    autoFocus
                    disabled={clientSaving}
                    onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") void saveClient(r);
                      if (e.key === "Escape") setEditingId(null);
                    }}
                  />
                </td>
                <td style={{ padding: "10px 12px" }}>
                  <Input
                    aria-label={`Billing email for ${r.name}`}
                    type="email"
                    value={draft.email}
                    maxLength={254}
                    disabled={clientSaving}
                    onChange={(e) => setDraft((d) => ({ ...d, email: e.target.value }))}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") void saveClient(r);
                      if (e.key === "Escape") setEditingId(null);
                    }}
                  />
                </td>
                <td style={{ padding: "10px 12px" }}>
                  <Select
                    aria-label={`Status for ${r.name}`}
                    value={draft.status}
                    disabled={clientSaving}
                    onChange={(e) => setDraft((d) => ({ ...d, status: e.target.value }))}
                  >
                    {CLIENT_STATUSES.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </Select>
                </td>
                <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                  <span style={{ display: "inline-flex", gap: 8 }}>
                    <Button variant="primary" onClick={() => saveClient(r)} disabled={clientSaving || !draft.name.trim() || !draft.email.trim()}>
                      Save
                    </Button>
                    <Button variant="secondary" onClick={() => setEditingId(null)} disabled={clientSaving}>
                      Cancel
                    </Button>
                  </span>
                </td>
              </tr>
            ) : (
              <tr key={r.id} style={{ borderBottom: "1px solid var(--rc-border-soft)" }}>
                <td style={{ padding: "10px 12px" }}>{r.name}</td>
                <td style={{ padding: "10px 12px" }}>{r.billing_email}</td>
                <td style={{ padding: "10px 12px" }}>{r.status}</td>
                <td style={{ padding: "10px 12px" }}>
                  <button
                    aria-label={`Edit ${r.name}`}
                    style={{ background: "none", border: "none", padding: 0, color: "var(--rc-primary)", cursor: "pointer", fontSize: 13 }}
                    onClick={() => startEdit(r)}
                  >
                    Edit
                  </button>
                </td>
              </tr>
            )
          )}
        </tbody>
      </table></div>
      {clientError && <p role="alert" style={{ color: "var(--rc-danger)", marginTop: 12 }}>{clientError}</p>}
      {clientMessage && <p style={{ color: "var(--rc-success)", marginTop: 12 }}>{clientMessage}</p>}
      <p style={{ color: "var(--rc-text-faint)", marginTop: 16, marginBottom: 32, fontSize: 13 }}>
        The name and billing email are what an invoice shows and where it is emailed. Only active clients are invoiced; a client's calls follow its licence, not
        this status. Creating new clients and businesses isn't built yet. Once a client's business exists, invite its first owner below.
      </p>

      <BusinessIndustries />

      <h2 className="rc-h2" style={{ marginTop: 32, marginBottom: 12 }}>Business logins</h2>
      {businesses.length === 0 ? (
        <p style={{ color: "var(--rc-text-faint)" }}>No businesses yet.</p>
      ) : (
        <form onSubmit={handleInvite} style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
          <label style={{ fontSize: 13 }}>
            Business
            <select
              value={businessId}
              onChange={(e) => setBusinessId(e.target.value)}
              style={{ display: "block", padding: 6, marginTop: 4 }}
            >
              {businesses.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name} ({b.clients?.name ?? "-"})
                </option>
              ))}
            </select>
          </label>
          <label style={{ fontSize: 13 }}>
            Email
            <input
              required
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{ display: "block", padding: 6, marginTop: 4 }}
            />
          </label>
          <label style={{ fontSize: 13 }}>
            Permission
            <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value as "owner" | "manager" | "viewer")} style={{ display: "block", padding: 6, marginTop: 4 }}>
              <option value="owner">Owner</option>
              <option value="manager">Manager</option>
              <option value="viewer">Viewer (read-only)</option>
            </select>
          </label>
          <button type="submit" disabled={saving} style={{ padding: "8px 16px" }}>
            Invite
          </button>
        </form>
      )}
      {businessId && (
        <div style={{ marginTop: 12, fontSize: 13 }}>
          {members === null ? (
            <span style={{ color: "var(--rc-text-muted)" }}>Loading...</span>
          ) : members.length === 0 ? (
            <span style={{ color: "var(--rc-text-muted)" }}>No logins yet.</span>
          ) : (
            <table style={{ borderCollapse: "collapse" }}>
              <tbody>
                {members.map((m) => (
                  <tr key={m.user_id}>
                    <td style={{ padding: "4px 16px 4px 0" }}>{m.email || m.phone || "(no email)"}</td>
                    <td style={{ padding: "4px 0" }}>
                      <select value={m.role} onChange={(e) => void changeRole(m, e.target.value)} style={{ padding: 4 }}>
                        <option value="owner">Owner</option>
                        <option value="manager">Manager</option>
                        <option value="viewer">Viewer (read-only)</option>
                        {m.role === "staff" && <option value="staff">Staff</option>}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p style={{ color: "var(--rc-text-muted)", margin: "8px 0 0" }}>
            A free online-booking client is a viewer. When they take the phone service, change their permission here to Manager or Owner; nothing else needs moving.
          </p>
        </div>
      )}
      {error && <p style={{ color: "var(--rc-danger)", marginTop: 12 }}>{error}</p>}
      {message && <p style={{ color: "var(--rc-success)", marginTop: 12 }}>{message}</p>}
    </div>
  );
}
